import { NavLink } from "react-router";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { getNavigationSectionsForRole } from "@/lib/routes/admin-route-catalog";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface AdminSidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
}

export default function AdminSidebar({
  mobileOpen = false,
  onClose,
  collapsed,
}: AdminSidebarProps) {
  const { user } = useAuth();
  const sections = user ? getNavigationSectionsForRole(user.role) : [];

  const handleNavClick = () => {
    onClose?.();
  };

  // collapsed === undefined → responsive (CSS handles it)
  // collapsed === true → forced icon-only (testing)
  // collapsed === false → forced expanded (testing)
  const isCollapsed = collapsed === true;
  const isExpanded = collapsed === false;

  const navContent = (
    <>
      <div
        className={cn(
          "flex h-[var(--admin-header-height)] items-center",
          collapsed === undefined
            ? "px-6 md:justify-center md:px-2 lg:justify-start lg:px-6"
            : isCollapsed
              ? "justify-center px-2"
              : "px-6",
        )}
      >
        {/* Monogram shown when collapsed */}
        <span
          className={cn(
            "text-xl font-bold text-white",
            collapsed === undefined
              ? "hidden md:block lg:hidden"
              : isCollapsed
                ? "block"
                : "hidden",
          )}
        >
          A
        </span>
        {/* Full wordmark shown when expanded */}
        <span
          className={cn(
            "text-xl font-bold text-white",
            collapsed === undefined ? "block md:hidden lg:block" : isExpanded ? "block" : "hidden",
          )}
        >
          ATS Ready
        </span>
        <span
          className={cn(
            "ml-2 rounded bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground",
            collapsed === undefined ? "block md:hidden lg:block" : isExpanded ? "block" : "hidden",
          )}
        >
          Admin
        </span>
      </div>

      <nav
        className={cn(
          "menu-transparent flex-1 overflow-y-auto py-4",
          collapsed === undefined ? "px-3 md:px-1.5 lg:px-3" : isCollapsed ? "px-1.5" : "px-3",
        )}
        role="navigation"
      >
        {sections.map((section) => (
          <div key={section.title} className="mb-4">
            <p
              className={cn(
                "mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40",
                collapsed === undefined ? "md:hidden lg:block" : isCollapsed ? "hidden" : "block",
              )}
            >
              {section.title}
            </p>
            {section.items.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === "/"}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  cn(
                    "menu-item mb-0.5 flex items-center rounded-xl py-2 text-sm font-medium transition-colors",
                    collapsed === undefined
                      ? "gap-3 px-3 md:justify-center md:gap-0 md:px-2 lg:justify-start lg:gap-3 lg:px-3"
                      : isCollapsed
                        ? "justify-center gap-0 px-2"
                        : "gap-3 px-3",
                    isActive
                      ? "bg-primary-subtle text-primary"
                      : "text-white/70 hover:bg-[hsl(var(--sidebar-accent))] hover:text-white",
                  )
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {isCollapsed ? (
                  <span className="sr-only">{item.name}</span>
                ) : isExpanded ? (
                  <span>{item.name}</span>
                ) : (
                  <span className="md:hidden lg:inline">{item.name}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div
        className={cn(
          "border-t border-[hsl(var(--sidebar-border))] p-3",
          collapsed === undefined ? "md:hidden lg:block" : isCollapsed ? "hidden" : "block",
        )}
      >
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
          <p className="font-semibold text-white/90">Admin Panel</p>
          <p className="mt-1">Manage users, subscriptions, and AI operations.</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:flex md:flex-col md:border-r md:border-[hsl(var(--sidebar-border))] md:bg-[hsl(var(--sidebar))] md:text-[hsl(var(--sidebar-foreground))]",
          "md:w-16 lg:w-[var(--admin-sidebar-width)]",
        )}
      >
        {navContent}
      </aside>

      {/* Mobile drawer */}
      <Dialog open={mobileOpen} onOpenChange={(nextOpen) => !nextOpen && onClose?.()}>
        <DialogContent
          variant="sheet"
          side="left"
          hideClose
          data-testid="admin-mobile-nav"
          aria-label="Admin navigation"
          className="h-dvh w-[min(88vw,22rem)] border-r border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar))] p-0 text-[hsl(var(--sidebar-foreground))] md:hidden"
        >
          {navContent}
        </DialogContent>
      </Dialog>
    </>
  );
}
